/* eslint-disable @next/next/no-img-element */
'use client'
import { Icon, Image } from '@/components'
import { AvatarWithStatus, AvatarWithStatusProps } from '@/components/common/live/avatar'
import { accountMenuList } from '@/components/pages/accounts/aside/menu.config'
import { CALLBACK_URL_KEY } from '@/constants'
import { useLogout } from '@/features/auth'
import Link from '@/features/ref/components/Link'
import { useCookieHttpOnly, useCopyToClipboard } from '@/hooks'
import { usePathname } from '@/libs/i18n/routing'
import { useAccountStore } from '@/store'
import { getTheImageUrl, getVND } from '@/utils/helpers'
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/Logout'
import { Box, BoxProps, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslations } from 'next-intl'
import React, { useState } from 'react'
interface UserLoginProps {
  props?: Omit<BoxProps, 'children'>
  userProps?: {
    wrappedProps?: React.HTMLAttributes<HTMLDivElement>
    avtProps?: AvatarWithStatusProps['avtProps']
    textProps?: React.HTMLAttributes<HTMLDivElement>
  }
}
export const UserLogin = ({ userProps, props }: UserLoginProps) => {
  const { isAuthenticated, data, isLoading } = useAccountStore()
  // Hook lấy cookie HttpOnly ( nếu muốn xoá hãy hỏi Ngọc Nhật )
  const { refetchCookie } = useCookieHttpOnly({
    cookieName: 'token',
  })
  const t = useTranslations()
  const { copy } = useCopyToClipboard()
  const { logout: handleLogout } = useLogout()
  const theme = useTheme()
  const pathname = usePathname()
  const listMenu = accountMenuList?.filter((item) => item.isMainBar)
  const [isCopied, setIsCopied] = useState(false)

  return (
    <Box {...props}>
      {isAuthenticated && (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            '&:hover .dropdown-menu': {
              opacity: 1,
              visibility: 'visible',
              transform: 'translateY(0)',
            },
          }}
        >
          {/* User Trigger */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              borderRadius: 1,
              flexDirection: {
                xs: 'row-reverse',
                md: 'row',
              },
              padding: 0.5,
              transition: 'background-color 0.2s',
            }}
            {...userProps?.wrappedProps}
          >
            <Link href={'/accounts'}>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    md: 'flex',
                  },
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                }}
                {...userProps?.textProps}
              >
                <Typography
                  sx={{
                    fontSize: 'inherit',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                  fontWeight={300}
                >
                  {data?.lastname}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {data.affiliate_information?.total_wallet_money > 0 ? (
                    <Typography sx={{ fontSize: 'inherit' }} fontWeight={600}>
                      {getVND(data.affiliate_information?.total_wallet_money || 0, ' ')}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '.8em',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        background: 'linear-gradient(90deg, #ffe259, #ffb347, #ffe259)',
                        backgroundSize: '300% 300%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'moveGradient 4s linear infinite',
                        '@keyframes moveGradient': {
                          '0%': { backgroundPosition: '0% 50%' },
                          '50%': { backgroundPosition: '100% 50%' },
                          '100%': { backgroundPosition: '0% 50%' },
                        },
                      }}
                    >
                      Tích lũy{' '}
                      <img
                        src="/assets/images/ic-price.png"
                        alt=""
                        className="relative top-[-1px] inline-block w-[1em] flex-shrink-0 align-middle"
                      />{' '}
                      ngay
                    </Typography>
                  )}
                  {data.affiliate_information?.total_wallet_money > 0 && (
                    <img
                      src="/assets/images/ic-price.png"
                      alt=""
                      className="w-[1em] flex-shrink-0 align-middle"
                    />
                  )}
                </Box>
              </Box>
            </Link>
            <Link href={'/accounts'}>
              <AvatarWithStatus
                avtProps={{
                  sx: { width: { xs: 28, md: 36 }, height: { xs: 28, md: 36 } },
                  ...userProps?.avtProps,
                }}
                alt="avt"
                isHide
                src={data?.profile_picture}
              />
            </Link>
          </Box>

          {/* Dropdown Menu */}
          <Box
            className="dropdown-menu"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: '100%',
              right: 0,
              minWidth: 250,
              backgroundColor: 'background.default',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              opacity: 0,
              visibility: 'hidden',
              transform: 'translateY(-10px)',
              transition: 'all 0.2s ease-in-out',
              zIndex: 1000,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {/* User Info Header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {data?.lastname || '---'}
                <Image
                  sx={{ width: { xs: '4rem', lg: '5rem' }, display: 'inline-block', ml: 0.5 }}
                  src={getTheImageUrl(data?.affiliate_information?.image)}
                  alt=""
                  slotProps={{ img: { style: { display: 'block' } } }}
                />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data?.email}
              </Typography>
            </Box>

            {/* Menu Items */}
            <Box sx={{ py: 0.5 }}>
              {listMenu.map((item) => (
                <Link key={item.path} href={item.path} passHref>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      py: 1,
                      gap: 1.5,
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText',
                      },
                    }}
                  >
                    {item.icon}
                    <Stack>
                      <Typography variant="body2">{item.label}</Typography>
                      {item.path === '/affiliate/dashboard' && data?.increment_id && (
                        <Box
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            copy(`${window.location.origin}/?ref=${data?.increment_id}`)
                            setIsCopied(true)
                            setTimeout(() => setIsCopied(false), 2000)
                          }}
                          sx={{
                            fontSize: '0.875rem',
                            color: isCopied ? 'success.main' : 'four.main',
                            cursor: 'pointer',
                            transition: 'color 0.3s ease',
                            '&:hover': {
                              color: isCopied ? 'success.main' : 'four.main',
                            },
                          }}
                        >
                          Link affiliate{' '}
                          {isCopied ? (
                            <CheckOutlinedIcon
                              sx={{
                                fontSize: '0.875rem',
                                verticalAlign: 'middle',
                                color: 'success.main',
                                animation: 'scale-in 0.3s ease',
                              }}
                            />
                          ) : (
                            <ContentCopyOutlinedIcon sx={{ fontSize: '0.875rem', verticalAlign: 'middle' }} />
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Link>
              ))}

              {/* Divider */}
              <Box sx={{ my: 0.5, mx: 2, height: 1, backgroundColor: 'divider' }} />

              {/* Logout */}
              <Box
                onClick={handleLogout}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  gap: 1.5,
                  cursor: 'pointer',
                  color: 'error.main',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <LogoutOutlinedIcon fontSize="small" />
                <Typography variant="body2">{t('UserProfile.sign_out')}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {!isAuthenticated && (
        <Link
          aria-label="login"
          href={'/login?' + CALLBACK_URL_KEY + '=' + encodeURIComponent(pathname)}
          // onClick={() => refetchCookie()}
        >
          {isLoading ? (
            <Skeleton variant="circular" width={40} height={40} />
          ) : (
            <IconButton
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'var(--size-hd-icon)',
                height: 'var(--size-hd-icon)',
              }}
              aria-label="login"
            >
              <Icon.UserV2
                sx={{
                  fontSize: '1.2rem',
                }}
                className="icon"
              />
            </IconButton>
          )}
        </Link>
      )}
    </Box>
  )
}
